import React from 'react';
import { Box, Typography, TextField, FormControl } from '@mui/material';

import { CommonCement } from '../common/common-cement';

import type {
  WitnessCompanyDetailForm,
  WitnessCompanyRegistrationForm,
} from '../../types/company.types';

import { getStylesForEditableField } from '@/utils/helpers';
import { PaperBoxStyles } from '@/components/styles';

// Union type to support both registration and detail forms
type WitnessFormData = WitnessCompanyDetailForm | WitnessCompanyRegistrationForm;

interface WitnessInfoSectionProps {
  formData: WitnessFormData;
  originalFormData: WitnessFormData | null;
  onUpdateField: <K extends keyof WitnessFormData>(field: K, value: WitnessFormData[K]) => void;
  getFieldError: (fieldName: string) => string | undefined;
  isFieldValid: (fieldName: string) => boolean;
}
export const WitnessInfoSection: React.FC<WitnessInfoSectionProps> = ({
  formData,
  originalFormData,
  onUpdateField,
  getFieldError,
  isFieldValid,
}) => {
  return (
    <PaperBoxStyles elevation={1} sx={{ mb: 3, paddingBottom: '32px' }}>
      <Box sx={{ mt: 3 }}>
        <Typography
          variant='h5'
          sx={{
            color: '#1A1C20',
            fontSize: '22px',
            fontWeight: 600,
            mb: 3,
          }}
        >
          立会会社基本情報
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
          *は必須項目です。
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Official Name */}
          <FormControl fullWidth error={!isFieldValid('officialName')}>
            <TextField
              label='正式名称'
              value={formData.officialName}
              onChange={(e) => onUpdateField('officialName', e.target.value)}
              error={!isFieldValid('officialName')}
              helperText={getFieldError('officialName')}
              placeholder='正式名称を入力'
              required
              slotProps={{
                input: {
                  inputProps: { maxLength: 100 },
                  sx: getStylesForEditableField(
                    !!originalFormData,
                    originalFormData?.officialName,
                    formData.officialName
                  ),
                },
              }}
            />
          </FormControl>

          <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
            {/* Phone */}
            <FormControl fullWidth error={!isFieldValid('tel')}>
              <TextField
                label='電話'
                value={formData.tel}
                onChange={(e) => onUpdateField('tel', e.target.value)}
                error={!isFieldValid('tel')}
                helperText={getFieldError('tel')}
                placeholder='電話番号を入力'
                required
                slotProps={{
                  input: {
                    inputProps: { maxLength: 20, pattern: '[0-9]*', inputMode: 'numeric' },
                    sx: getStylesForEditableField(
                      !!originalFormData,
                      originalFormData?.tel,
                      formData.tel
                    ),
                  },
                }}
              />
            </FormControl>

            {/* FAX */}
            <FormControl fullWidth error={!isFieldValid('fax')}>
              <TextField
                label='FAX'
                value={formData.fax}
                onChange={(e) => onUpdateField('fax', e.target.value)}
                error={!isFieldValid('fax')}
                helperText={getFieldError('fax')}
                placeholder='FAX番号を入力'
                required
                slotProps={{
                  input: {
                    inputProps: { maxLength: 20, pattern: '[0-9]*', inputMode: 'numeric' },
                    sx: getStylesForEditableField(
                      !!originalFormData,
                      originalFormData?.fax,
                      formData.fax
                    ),
                  },
                }}
              />
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
            {/* Contact Person */}
            <FormControl fullWidth error={!isFieldValid('contactPerson')}>
              <TextField
                label='連絡責任者'
                value={formData.contactPerson || ''}
                onChange={(e) => onUpdateField('contactPerson', e.target.value)}
                error={!isFieldValid('contactPerson')}
                helperText={getFieldError('contactPerson')}
                placeholder='氏名を入力'
                slotProps={{
                  input: {
                    inputProps: { maxLength: 50 },
                    sx: getStylesForEditableField(
                      !!originalFormData,
                      originalFormData?.contactPerson,
                      formData.contactPerson
                    ),
                  },
                }}
              />
            </FormControl>

            {/* Contact Person TEL */}
            <FormControl fullWidth error={!isFieldValid('contactTel')}>
              <TextField
                label='連絡責任者TEL'
                value={formData.contactTel || ''}
                onChange={(e) => onUpdateField('contactTel', e.target.value)}
                error={!isFieldValid('contactTel')}
                helperText={getFieldError('contactTel')}
                placeholder='電話番号を入力'
                slotProps={{
                  input: {
                    inputProps: { maxLength: 20, pattern: '[0-9]*', inputMode: 'numeric' },
                    sx: getStylesForEditableField(
                      !!originalFormData,
                      originalFormData?.contactTel,
                      formData.contactTel
                    ),
                  },
                }}
              />
            </FormControl>
          </Box>

          {/* セメント共通 */}
          <CommonCement
            value={formData.isCommonCement}
            slotProps={{
              input: {
                sx: getStylesForEditableField(
                  !!originalFormData,
                  originalFormData?.isCommonCement,
                  formData.isCommonCement
                ),
              },
            }}
            onChange={(value) => onUpdateField('isCommonCement', value)}
          />
        </Box>
      </Box>
    </PaperBoxStyles>
  );
};
